/**
 * Client-side half of the upload mapping builder: read an example CSV/XLSX
 * into a grid of strings, name its columns from the chosen header row, and
 * show the first rows as the generic tabular parser would read them with a
 * given `upload_mapping`.
 */
import * as XLSX from 'xlsx';
import type { UploadMapping } from '@mariana/labos-client';

/** Rows of cells as text, in file order; row 1 of the file is grid[0]. */
export type Grid = string[][];

export const PREVIEW_ROW_LIMIT = 5;

export function gridFromBuffer(data: ArrayBuffer | Uint8Array): Grid {
  const workbook = XLSX.read(data instanceof Uint8Array ? data : new Uint8Array(data), { type: 'array', raw: false });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return [];
  const sheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: false, defval: '', blankrows: false });
  return rows.map(row => row.map(cell => (cell === null || cell === undefined ? '' : String(cell).trim())));
}

export async function gridFromFile(file: File): Promise<Grid> {
  return gridFromBuffer(await file.arrayBuffer());
}

/** Column names from the 1-based header row; blank headers are named by position so they stay selectable. */
export function headersOf(grid: Grid, headerRow: number): string[] {
  const row = grid[headerRow - 1] ?? [];
  return row.map((cell, i) => (cell.trim() ? cell.trim() : `Column ${i + 1}`));
}

export interface PreviewRow {
  sampleId: string;
  /** analyte key → the entry the parser would write. */
  entries: Record<string, { value: string; unit?: string }>;
}

/**
 * The first data rows under the header, read through the mapping: the sample
 * id column gives `sampleId`, every mapped column gives an entry under its
 * analyte key. Empty cells give no entry. Rows with a blank sample id are
 * skipped, as the parser skips them.
 */
export function previewRows(grid: Grid, mapping: Pick<UploadMapping, 'headerRow' | 'sampleIdColumn' | 'columns'>, limit = PREVIEW_ROW_LIMIT): PreviewRow[] {
  const headerRow = mapping.headerRow ?? 1;
  const headers = headersOf(grid, headerRow);
  const index = (name: string) => headers.indexOf(name);
  const sampleIdx = index(mapping.sampleIdColumn);
  if (sampleIdx < 0) return [];
  const mapped = mapping.columns
    .map(c => ({ ...c, idx: index(c.column) }))
    .filter(c => c.idx >= 0);
  const out: PreviewRow[] = [];
  for (let r = headerRow; r < grid.length && out.length < limit; r++) {
    const row = grid[r];
    const sampleId = (row[sampleIdx] ?? '').trim();
    if (!sampleId) continue;
    const entries: PreviewRow['entries'] = {};
    for (const c of mapped) {
      const value = (row[c.idx] ?? '').trim();
      if (!value) continue;
      entries[c.key] = c.unit ? { value, unit: c.unit } : { value };
    }
    out.push({ sampleId, entries });
  }
  return out;
}
